import { hasProperty } from '@fmnts/core';
import { MonoTypeOperatorFunction, OperatorFunction } from 'rxjs';
import { filter, map } from 'rxjs';
import { ActionRequest } from './action-request';

export enum ActionEventType {
  /**
   * When the action did start
   */
  Start,
  /**
   * When an action handler handled the request
   */
  Response,
}

/**
 * An event that is sent when an action is started.
 */
export class ActionStartEvent {
  readonly type = ActionEventType.Start;

  constructor(public readonly request: ActionRequest<unknown>) {}
}

export abstract class ActionResponseBase {
  public readonly type = ActionEventType.Response;

  constructor(
    /**
     * The action request for which this response
     * is emitted
     */
    public readonly request: ActionRequest<unknown>,
  ) {}
}

/**
 * Represents a successful execution of an `ActionRequest`.
 */
export class ActionResponse<T> extends ActionResponseBase {
  constructor(
    req: ActionRequest<unknown>,
    /**
     * The result of the action
     */
    public readonly result: T,
  ) {
    super(req);
  }
}

/**
 * Represents an error that was encountered during
 * handling an `ActionRequest`.
 */
export class ActionResponseError extends ActionResponseBase implements Error {
  public readonly name = 'ActionResponseError';

  constructor(
    req: ActionRequest<unknown>,
    /**
     * Error message
     */
    public readonly message: string,
  ) {
    super(req);
  }
}

/**
 * Any event emitted by an `ActionHandler`
 */
export type ActionEvent<T> = ActionResponse<T> | ActionStartEvent;

// --------------------------------------------------------
// Type guards
// --------------------------------------------------------
function hasEventType<E extends ActionEventType>(type: E) {
  return hasProperty('type', (t): t is E => t === type);
}

/**
 * Type guard for action response
 */
export function isActionResponse(u: unknown): u is ActionResponse<unknown> {
  return u instanceof ActionResponse;
}

const hasActionStartEventType = hasEventType(ActionEventType.Start);

/**
 * Type guard for `ActionStartEvent`.
 *
 * @param u ActionEvent to check
 *
 * @returns
 * `true` if `u` is of type `ActionStartEvent`.
 */
export function isActionStartEvent(u: unknown): u is ActionStartEvent {
  return u instanceof ActionStartEvent || hasActionStartEventType(u);
}

// --------------------------------------------------------
// Operators
// --------------------------------------------------------
/**
 * @param action
 * Action that should be emitted.
 * You may also provide a `RegExp` if you want to check for
 * multiple actions.
 *
 * @returns
 * An Operator that takes a stream of `ActionEvents` as a source
 * and emits events with a matching `request.action`.
 */
export function filterActionEventByAction<T>(
  action: string | RegExp,
): MonoTypeOperatorFunction<ActionEvent<T>> {
  const rex = typeof action === 'string' ? new RegExp(`^${action}$`) : action;

  return filter((e) => rex.test(e.request.action));
}

/**
 * @param types Types to filter
 *
 * @returns
 * An operator that takes a stream of `ActionEvent`s as a source
 * and only emits those `ActionEvent`s which `type` matches one
 * of the supplied `types`.
 */
export function filterActionEventByTypes<T>(
  types: ActionEventType[],
): MonoTypeOperatorFunction<ActionEvent<T>> {
  return filter((ev) => types.includes(ev.type));
}

/**
 * @returns
 * An operator that takes a stream of `ActionEvent`s as a source and emits
 * with a boolean value indicating if the action is running or has finished.
 */
export function mapIsRunning<T>(): OperatorFunction<ActionEvent<T>, boolean> {
  return (obs$) =>
    obs$.pipe(
      // Make sure to ignore all other events
      filterActionEventByTypes([
        ActionEventType.Start,
        ActionEventType.Response,
      ]),
      // Map to boolean value
      map((ev) => ev.type === ActionEventType.Start),
    );
}
